import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, ChevronUp, ChevronDown, Download, Upload, Columns2, SquarePen, FileText } from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";
import DocumentSheet from "@/components/DocumentSheet";

/** Move an item within a list — the field order is what the customer sees, so it is data. */
const move = (list, from, to) => {
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};
import { AdminTable } from "./lib";
import { ICON_NAMES } from "@/lib/serviceGroups";

const GRID = "grid-cols-[minmax(240px,1fr)_160px_160px_90px]";

// The Document body tab's layouts: both panes, or either one alone at full width and full height.
// "preview" is the one for judging the page — with the whole dialog to itself the sheet is fitted
// to the pane in both directions, so an entire A4 page shows at once rather than a column-width
// slice of it.
const BODY_VIEWS = [
  { key: "split", icon: Columns2, label: "Source and preview side by side" },
  { key: "source", icon: SquarePen, label: "Expand source" },
  { key: "preview", icon: FileText, label: "Expand preview — see the whole A4 page" },
];

// An expanded pane's height: the dialog (92vh) less its title, tabs, notice and buttons, with a
// floor so a short window never gets a pane smaller than the side-by-side one.
const BODY_EXPANDED_H = "h-[max(420px,calc(92vh-260px))]";

/** The layout switch. Rendered in both panes' headers, so it is reachable whichever one is showing. */
function BodyViewToggle({ view, onChange, where }) {
  return (
    <div className="flex items-center gap-0.5" role="group" aria-label="Layout">
      {BODY_VIEWS.map(({ key, icon: Icon, label }) => (
        <button key={key} type="button" title={label} aria-label={label} aria-pressed={view === key}
                data-testid={`tpl-view-${key}-${where}`}
                // mouseDown + preventDefault, like the editor's own buttons, so clicking this does
                // not take focus (and the caret) out of the text being edited.
                onMouseDown={(e) => { e.preventDefault(); onChange(key); }}
                onClick={() => onChange(key)}
                className={`p-1.5 rounded transition-colors ${view === key
                  ? "text-[#CBA96C] bg-white/10"
                  : "text-[#A0AABF] hover:text-white hover:bg-white/10"}`}>
          <Icon className="w-3.5 h-3.5"/>
        </button>
      ))}
    </div>
  );
}
const COLUMNS = [
  { key: "title", label: "Title" },
  { key: "group", label: "Group" },
  { key: "fields", label: "Fields" },
  { key: "actions", label: "Actions" },
];

const BLANK = { key: "", title: "", group: "affidavit", fields: [], body_markdown: "" };
const FIELD_TYPES = [
  { value: "text", label: "String" },
  { value: "number", label: "Number" },
  { value: "date", label: "Calendar" },
  { value: "list", label: "List" },
];
const NEW_FIELD = { key: "", label: "", type: "text", options: "", max_length: "" };

// The JSON shape a template round-trips through, for anyone editing it *outside* this app — e.g.
// handing this exact shape (or a real export, which is the same thing) to an LLM with a source
// document and asking for a filled-in version back. It is not a separate format invented for this:
// it is precisely what `Export JSON` produces and `Import JSON` accepts, which is in turn precisely
// the body `PUT /admin/templates/{key}` takes (see backend/routers/admin.py::update_template) — one
// shape, so this sample can never drift from what the server actually requires.
/** The in-form row shape (options as one comma-separated string) from either a server response or
 *  an imported JSON file (where options may already be an array). Tolerates a bare string field
 *  list too, the same "legacy" shape `normalize_field` accepts server-side. */
const toEditableField = (f) => {
  if (typeof f === "string") return { key: f, label: "", type: "text", options: "", max_length: "" };
  const opts = f.options;
  return {
    key: f.key || "", label: f.label || "", type: f.type || "text",
    options: Array.isArray(opts) ? opts.join(", ") : (opts || ""),
    max_length: f.max_length ?? "",
  };
};

/** The portable export shape — options back to a real array, so the file that comes out is exactly
 *  what a fresh `Import JSON` (or a hand-written one, or an LLM's) is expected to look like. */
const toPortableField = (f) => ({
  key: f.key, label: f.label, type: f.type,
  options: f.type === "list" ? (f.options || "").split(",").map(s => s.trim()).filter(Boolean) : undefined,
  max_length: f.max_length !== "" && f.max_length != null ? Number(f.max_length) : undefined,
});

/**
 * Template Catalog — the single place a document's wording is defined.
 *
 * Templates are deliberately un-versioned: an edit takes effect everywhere, immediately. The old
 * snapshot/restore machinery was removed because nothing ever consulted a transaction's pinned
 * version, so "restore" silently rewrote live customer documents rather than protecting them.
 */
export function AdminTemplates() {
  const [rows, setRows] = useState([]);
  const [groups, setGroups] = useState([]);
  const [editing, setEditing] = useState(null); // { key, title, group, body_markdown, fields }
  const [bodyView, setBodyView] = useState("split"); // see BODY_VIEWS
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [groupFilter, setGroupFilter] = useState("all");
  const [editGroups, setEditGroups] = useState(null); // draft list while the dialog is open
  const load = () => api.get("/admin/templates").then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/template-groups").then(r => setGroups(r.data)).catch(() => {}); }, []);
  const groupLabel = (g) => groups.find(x => x.key === g)?.label || g || "—";
  const saveGroups = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/template-groups", editGroups);
      setGroups(data); setEditGroups(null); toast.success("Groups saved"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save groups"); }
    finally { setBusy(false); }
  };
  const del = async (key) => {
    if (!window.confirm(`Delete the "${key}" template?

Customers can no longer order it. Existing transactions keep their own data, and any document you have already edited keeps your edit. To retire a template customers may be mid-draft on, switch it off in Pricing & Availability instead.`)) return;
    try { await api.delete(`/admin/templates/${key}`); toast.success("Template deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  // Bumped whenever body_markdown is set from *outside* the rich editor's own typing (opening a
  // different template, starting a new one, Import JSON) — RichTextEditor remounts on this key,
  // which is how it picks up a genuinely new value without React fighting the browser for control
  // of the contentEditable on every keystroke in between. See RichTextEditor.jsx.
  const [resetToken, setResetToken] = useState(0);
  const openEdit = (r) => {
    setCreating(false);
    setEditing({ key: r.key, title: r.title || "", group: r.group || "affidavit", body_markdown: r.body_markdown || "", fields: (r.fields || []).map(toEditableField) });
    setResetToken((t) => t + 1);
  };
  const openCreate = () => { setCreating(true); setEditing({ ...BLANK }); setResetToken((t) => t + 1); };
  const filePicker = useRef(null);

  const exportJson = () => {
    const payload = {
      ...(creating ? { key: editing.key || undefined } : {}),
      title: editing.title, group: editing.group,
      fields: (editing.fields || []).filter(f => (f.key || "").trim()).map(toPortableField),
      body_markdown: editing.body_markdown,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${editing.key || "template"}.json`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };
  const importJson = (e) => {
    const file = e.target.files[0]; e.target.value = ""; if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      let parsed;
      try { parsed = JSON.parse(reader.result); }
      catch { toast.error("That file isn't valid JSON"); return; }
      if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
        toast.error("Expected a single JSON object — see the shape below"); return;
      }
      if (parsed.fields !== undefined && !Array.isArray(parsed.fields)) {
        toast.error('"fields" must be a list'); return;
      }
      setEditing((cur) => ({
        ...cur,
        // The key is permanent once a template exists, so an import can only set it while
        // creating — importing over an existing template must not silently change its identity.
        key: creating ? (parsed.key ? String(parsed.key).toLowerCase().replace(/\s+/g, "_") : cur.key) : cur.key,
        title: parsed.title ?? cur.title,
        group: parsed.group ?? cur.group,
        body_markdown: parsed.body_markdown ?? cur.body_markdown,
        fields: Array.isArray(parsed.fields) ? parsed.fields.map(toEditableField) : cur.fields,
      }));
      if (parsed.body_markdown !== undefined) setResetToken((t) => t + 1);
      toast.success("Imported — review below, then save.");
    };
    reader.readAsText(file);
  };
  const saveEdit = async () => {
    // The key is the template's identity: it is what a transaction stores as its subtype and what
    // every lookup resolves by, so it cannot change once created and cannot collide.
    if (creating) {
      const key = editing.key.trim();
      if (!/^[a-z][a-z0-9_]*$/.test(key)) { toast.error("Key must be lowercase letters, digits and underscores, starting with a letter"); return; }
      if (rows.some(r => r.key === key)) { toast.error(`A template with the key "${key}" already exists`); return; }
      if (!editing.title.trim()) { toast.error("Give the template a title"); return; }
    }
    const keys = (editing.fields || []).map(f => (f.key || "").trim()).filter(Boolean);
    if (new Set(keys).size !== keys.length) { toast.error("Two fields share the same key"); return; }
    const badKey = keys.find(k => !/^[a-z][a-z0-9_]*$/.test(k));
    if (badKey) { toast.error(`Field key "${badKey}" — lowercase letters, digits and underscores`); return; }
    // The fields and the body must describe the same document — the server refuses a save that
    // breaks that, so catch it here where we can point at which side is wrong.
    const tokens = new Set([...(editing.body_markdown || "").matchAll(/\{\{\s*(\w+)\s*\}\}/g)].map(m => m[1]));
    const unused = keys.filter(k => !tokens.has(k));
    const undefinedTokens = [...tokens].filter(t => !keys.includes(t));
    if (unused.length) {
      toast.error(`Not used in the document body: ${unused.map(k => `{{${k}}}`).join(", ")} — remove the field, or reference it in the body.`, { duration: 9000 });
      return;
    }
    if (undefinedTokens.length) {
      toast.error(`The body references undefined fields: ${undefinedTokens.map(k => `{{${k}}}`).join(", ")} — add the field, or remove it from the body.`, { duration: 9000 });
      return;
    }
    setBusy(true);
    try {
      await api.put(`/admin/templates/${editing.key}`, {
        title: editing.title,
        group: editing.group,
        body_markdown: editing.body_markdown,
        fields: (editing.fields || []).filter(f => (f.key || "").trim()),
      });
      toast.success(creating
        ? "Template created — set its price and switch it on in Pricing & Availability to put it on sale."
        : "Template updated", { duration: creating ? 8000 : 4000 });
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-6" data-testid="admin-templates-page">
      <h1 className="font-serif-display text-3xl md:text-4xl text-white">Template Catalog</h1>
      {/* Group chips, with counts — the catalogue is 15 rows and growing, and admin almost always
          wants one service at a time. */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="tpl-group-tabs">
        {[{ key: "all", label: "All" }, ...groups].map(g => {
          const n = g.key === "all" ? rows.length : rows.filter(r => (r.group || "affidavit") === g.key).length;
          return (
            <button key={g.key} data-testid={`tpl-group-${g.key}`} onClick={() => setGroupFilter(g.key)}
              className={`px-4 py-1.5 rounded-full text-xs border transition-colors ${
                groupFilter === g.key
                  ? "bg-[#CBA96C] text-[#080C16] border-[#CBA96C] font-medium"
                  : "bg-transparent text-[#A0AABF] border-white/15 hover:text-white hover:border-white/30"
              }`}>{g.label} <span className="opacity-70">({n})</span></button>
          );
        })}
        <button data-testid="tpl-manage-groups" onClick={() => setEditGroups(groups.map(g => ({ ...g })))}
                className="px-3 py-1.5 rounded-full text-xs border border-dashed border-white/20 text-[#6B7280] hover:text-white hover:border-white/40 transition-colors">
          Manage groups
        </button>
      </div>
      <AdminTable
        testid="admin-templates" columns={COLUMNS} gridCols={GRID}
        rows={groupFilter === "all" ? rows : rows.filter(r => (r.group || "affidavit") === groupFilter)}
        rowKey={(r) => r.key} defaultSort="title" empty="No templates."
        searchPlaceholder="Search template title or key…"
        matches={(r, q) => [r.key, r.title, ...(r.fields || [])].filter(Boolean).join(" ").toLowerCase().includes(q)}
        toolbar={
          <Button data-testid="tpl-new" size="sm" onClick={openCreate}
                  className="bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190]">
            <Plus className="w-3.5 h-3.5 mr-1"/>New template
          </Button>
        }
        accessors={{
          title: (r) => (r.title || "").toLowerCase(),
          group: (r) => r.group || "",
          fields: (r) => (r.fields || []).length,
          actions: (r) => r.key,
        }}
        renderRow={(r) => (
          <>
            <div className="min-w-0 pt-1.5">
              <div className={`text-sm truncate ${r.enabled === false ? "text-[#6B7280] line-through" : "text-white"}`}
                   title={r.enabled === false ? "Not on sale — turn it on in Pricing & Availability" : undefined}>{r.title}</div>
              <div className="font-mono text-[10px] text-[#6B7280] truncate">{r.key}</div>
            </div>
            <div className="text-xs text-[#A0AABF] pt-1.5 truncate">{groupLabel(r.group)}</div>
            <div className="text-xs text-[#A0AABF] pt-1.5">
              {(r.fields || []).length} fields
              {/* A template with no body renders an empty document for the customer */}
              {!(r.body_markdown || "").trim() && (
                <span className="ml-2 text-[#F27373]" title="No document body — this template renders an empty document">no body</span>
              )}
            </div>
            <div className="pt-1 flex items-center gap-1">
              <button data-testid={`tpl-edit-${r.key}`} onClick={() => openEdit(r)} title="Edit template" aria-label="Edit template"
                      className="p-1.5 rounded-full text-[#A0AABF] hover:text-white hover:bg-white/5 transition-colors">
                <Pencil className="w-3.5 h-3.5"/>
              </button>
              <button data-testid={`tpl-del-${r.key}`} onClick={() => del(r.key)} title="Delete template" aria-label="Delete template"
                      className="p-1.5 rounded-full text-[#6B7280] hover:text-[#F27373] hover:bg-white/5 transition-colors">
                <Trash2 className="w-3.5 h-3.5"/>
              </button>
            </div>
          </>
        )}
      />

      <Dialog open={!!editGroups} onOpenChange={(o) => !o && setEditGroups(null)}>
        <DialogContent data-testid="tpl-groups-dialog" className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Document groups</DialogTitle></DialogHeader>
          <div className="text-xs text-[#6B7280]">
            A group is the service a document is offered under — it is also the transaction's
            service type and the rate pricing falls back to. <strong>The key is permanent</strong>:
            transactions store it, so edit the label rather than the key, and a group with
            templates in it cannot be removed.
          </div>
          <div className="space-y-3">
            {(editGroups || []).map((g, i) => {
              const inUse = rows.some(r => (r.group || "affidavit") === g.key);
              return (
                <div key={i} className="grid grid-cols-[130px_1fr_120px_36px] gap-2 items-start border rounded p-3">
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-[#6B7280]">Key</Label>
                    <Input data-testid={`grp-key-${i}`} value={g.key} disabled={inUse}
                           title={inUse ? "In use by templates — the key is permanent" : undefined}
                           onChange={e => setEditGroups(l => l.map((x, j) => j === i ? { ...x, key: e.target.value.toLowerCase().replace(/\s+/g, "_") } : x))}/>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-[#6B7280]">Label &amp; description</Label>
                    <Input data-testid={`grp-label-${i}`} value={g.label} placeholder="Shown on the customer's cards"
                           onChange={e => setEditGroups(l => l.map((x, j) => j === i ? { ...x, label: e.target.value } : x))}/>
                    <Input data-testid={`grp-desc-${i}`} value={g.description || ""} placeholder="One line under the title" className="mt-1.5"
                           onChange={e => setEditGroups(l => l.map((x, j) => j === i ? { ...x, description: e.target.value } : x))}/>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-[#6B7280]">Icon</Label>
                    <select data-testid={`grp-icon-${i}`} value={g.icon || "file"}
                            onChange={e => setEditGroups(l => l.map((x, j) => j === i ? { ...x, icon: e.target.value } : x))}
                            className="mt-1 w-full h-10 rounded-md border border-white/10 bg-[#080C16] text-white px-2 text-sm">
                      {ICON_NAMES.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <button data-testid={`grp-del-${i}`} disabled={inUse}
                          title={inUse ? "Templates still belong to this group" : "Remove group"}
                          onClick={() => setEditGroups(l => l.filter((_, j) => j !== i))}
                          className="mt-6 p-1.5 rounded-full text-[#6B7280] hover:text-[#F27373] hover:bg-black/5 disabled:opacity-30 disabled:hover:text-[#6B7280]">
                    <Trash2 className="w-3.5 h-3.5"/>
                  </button>
                </div>
              );
            })}
            <Button variant="outline" size="sm" data-testid="grp-add"
                    onClick={() => setEditGroups(l => [...l, { key: "", label: "", icon: "file", description: "" }])}>
              <Plus className="w-3.5 h-3.5 mr-1"/>Add group
            </Button>
          </div>
          <Button data-testid="grp-save" disabled={busy} onClick={saveGroups} className="w-full bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190]">
            {busy ? "Saving…" : "Save groups"}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="tpl-edit-dialog" className="max-w-6xl w-[95vw] max-h-[92vh] overflow-y-auto">
          <DialogHeader className="flex-row items-center justify-between gap-2 space-y-0">
            <DialogTitle>{creating ? "New template" : `Edit template — ${editing?.key}`}</DialogTitle>
            <div className="flex items-center gap-1.5 pr-6">
              <input ref={filePicker} type="file" accept=".json,application/json" className="hidden"
                     onChange={importJson} data-testid="tpl-import-file"/>
              <Button type="button" variant="outline" size="sm" data-testid="tpl-export-json" onClick={exportJson}>
                <Download className="w-3.5 h-3.5 mr-1"/>Export JSON
              </Button>
              <Button type="button" variant="outline" size="sm" data-testid="tpl-import-json"
                      onClick={() => filePicker.current?.click()}>
                <Upload className="w-3.5 h-3.5 mr-1"/>Import JSON
              </Button>
            </div>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              {/* Two tabs, not one long scroll — Details/Fields and Document body are edited at
                  different times (set up a template once, then iterate on its wording repeatedly)
                  and finding the body editor used to mean scrolling past a growing field list
                  every time. Both tabs share one Cancel/Save pair below, since a save always
                  writes the whole template regardless of which tab is showing. */}
              <Tabs defaultValue="details">
                <TabsList>
                  <TabsTrigger data-testid="tpl-tab-details" value="details">Details &amp; Fields</TabsTrigger>
                  <TabsTrigger data-testid="tpl-tab-body" value="body">Document body</TabsTrigger>
                </TabsList>
                <TabsContent value="details" className="space-y-3">
                  {creating && (
                    <div>
                      <Label>Key</Label>
                      <Input data-testid="tpl-edit-key" value={editing.key} placeholder="e.g. gift_deed"
                             onChange={e => setEditing({ ...editing, key: e.target.value.toLowerCase().replace(/\s+/g, "_") })}/>
                      <div className="text-xs text-[#6B7280] mt-1">
                        Permanent identifier — transactions store it. Lowercase letters, digits and underscores.
                      </div>
                      <div className="text-xs text-[#F5A524] bg-[#F5A524]/10 border border-[#F5A524]/40 rounded p-2 mt-2">
                        A new template is created <strong>off sale</strong>. Set its price and switch it
                        on in <strong>Pricing &amp; Availability</strong> when it's ready for customers.
                      </div>
                    </div>
                  )}
                  <div><Label>Title</Label><Input data-testid="tpl-edit-title" value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })}/></div>
                  <div>
                    <Label>Group</Label>
                    <select data-testid="tpl-edit-group" value={editing.group}
                            onChange={e => setEditing({ ...editing, group: e.target.value })}
                            className="mt-1 w-full h-10 rounded-md border border-white/10 bg-[#080C16] text-white px-3 text-sm">
                      {groups.map(g => <option key={g.key} value={g.key}>{g.label}</option>)}
                    </select>
                    <div className="text-xs text-[#6B7280] mt-1">
                      Which service this document is offered under. The group is also the transaction's
                      service type and the rate it falls back to for pricing.
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <Label>Fields</Label>
                      <Button type="button" variant="outline" size="sm" data-testid="fld-add"
                              onClick={() => setEditing(e => ({ ...e, fields: [...(e.fields || []), { ...NEW_FIELD }] }))}>
                        <Plus className="w-3.5 h-3.5 mr-1"/>Add field
                      </Button>
                    </div>
                    <div className="text-xs text-[#6B7280] mt-1 mb-2">
                      These are the questions the customer answers. <strong>Key</strong> is what{" "}
                      <code className="font-mono">{"{{key}}"}</code> matches in the body (the other tab) and is
                      stored on the transaction — changing it on a live template orphans answers already collected.
                    </div>
                    {(editing.fields || []).length === 0 && (
                      <div className="text-xs text-[#6B7280] border border-dashed rounded p-3">No fields yet.</div>
                    )}
                    <div className="space-y-2">
                      {(editing.fields || []).map((f, i) => {
                        const set = (patch) => setEditing(e => ({ ...e, fields: e.fields.map((x, j) => j === i ? { ...x, ...patch } : x) }));
                        return (
                          <div key={i} className="grid grid-cols-[46px_1fr_1fr_110px_1fr_90px_32px] gap-2 items-start border rounded p-2">
                            {/* Order is the order the customer answers them in — the wizard renders
                                this list top to bottom and pages it four at a time. */}
                            <div className="flex flex-col items-center pt-1">
                              <button type="button" data-testid={`fld-up-${i}`} disabled={i === 0}
                                      title="Move up" aria-label="Move field up"
                                      onClick={() => setEditing(e => ({ ...e, fields: move(e.fields, i, i - 1) }))}
                                      className="text-[#6B7280] hover:text-white disabled:opacity-25">
                                <ChevronUp className="w-3.5 h-3.5"/>
                              </button>
                              <span className="text-[9px] text-[#6B7280] leading-none">{i + 1}</span>
                              <button type="button" data-testid={`fld-down-${i}`} disabled={i === (editing.fields || []).length - 1}
                                      title="Move down" aria-label="Move field down"
                                      onClick={() => setEditing(e => ({ ...e, fields: move(e.fields, i, i + 1) }))}
                                      className="text-[#6B7280] hover:text-white disabled:opacity-25">
                                <ChevronDown className="w-3.5 h-3.5"/>
                              </button>
                            </div>
                            <Input data-testid={`fld-label-${i}`} value={f.label} placeholder="Name"
                                   onChange={e => set({ label: e.target.value })}/>
                            <Input data-testid={`fld-key-${i}`} value={f.key} placeholder="key_name" className="font-mono text-xs"
                                   onChange={e => set({ key: e.target.value.toLowerCase().replace(/\s+/g, "_") })}/>
                            <select data-testid={`fld-type-${i}`} value={f.type}
                                    onChange={e => set({ type: e.target.value })}
                                    className="h-10 rounded-md border border-white/10 bg-[#080C16] text-white px-2 text-sm">
                              {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                            <Input data-testid={`fld-options-${i}`} value={f.options || ""}
                                   placeholder={f.type === "list" ? "A, B, C" : "—"} disabled={f.type !== "list"}
                                   title={f.type === "list" ? "Comma-separated choices" : "Only for List fields"}
                                   onChange={e => set({ options: e.target.value })}/>
                            <Input data-testid={`fld-length-${i}`} type="number" min="1" value={f.max_length ?? ""}
                                   placeholder="Length" disabled={f.type === "date"}
                                   title={f.type === "date" ? "Not applicable to a date" : "Maximum length"}
                                   onChange={e => set({ max_length: e.target.value })}/>
                            <button type="button" data-testid={`fld-del-${i}`} title="Remove field"
                                    onClick={() => setEditing(e => ({ ...e, fields: e.fields.filter((_, j) => j !== i) }))}
                                    className="mt-2 text-[#6B7280] hover:text-[#F27373]">
                              <Trash2 className="w-3.5 h-3.5"/>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                    {(editing.fields || []).length > 0 && (
                      <div className="grid grid-cols-[46px_1fr_1fr_110px_1fr_90px_32px] gap-2 text-[10px] uppercase tracking-widest text-[#6B7280] mt-1 px-2">
                        <span>Order</span><span>Name</span><span>Key</span><span>Type</span><span>List options</span><span>Length</span><span/>
                      </div>
                    )}
                  </div>
                </TabsContent>
                <TabsContent value="body">
                  {/* Side by side by default — Source (what admin edits) and Preview (the actual
                      printed sheet) so typing and seeing the effect happen in the same glance.
                      Stacks on a narrow viewport instead of squeezing two columns into it.

                      Either pane can be expanded to the full dialog (BodyViewToggle, beside the
                      font Size control and above the preview). Expanded, it also takes the
                      dialog's height, so the preview fits a whole A4 page rather than a
                      column-width slice. The hidden pane is hidden with CSS, not unmounted: the
                      editor is uncontrolled, and remounting it would drop the caret and undo
                      history every time the layout changed. */}
                  <div className={`grid grid-cols-1 gap-3 ${bodyView === "split" ? "lg:grid-cols-2" : ""}`}>
                    <div className={`flex flex-col ${bodyView === "preview" ? "hidden" : ""}`}>
                      <div className="text-[10px] uppercase tracking-widest text-[#6B7280] mb-1">Source</div>
                      {/* A real editable surface, not a textarea over raw tags — see
                          RichTextEditor.jsx for how "type like Word, save as the same small HTML
                          allow-list" is made safe (paste forced to plain text; every edit
                          re-serialized through an allow-list, never trusted as raw innerHTML). */}
                      <div className={bodyView === "split" ? "h-[420px]" : BODY_EXPANDED_H}>
                        <RichTextEditor resetKey={resetToken} value={editing.body_markdown}
                                        onChange={(v) => setEditing((cur) => ({ ...cur, body_markdown: v }))}
                                        toolbarExtra={<BodyViewToggle view={bodyView} onChange={setBodyView} where="source"/>}/>
                      </div>
                    </div>
                    <div className={`flex flex-col ${bodyView === "source" ? "hidden" : ""}`}>
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="text-[10px] uppercase tracking-widest text-[#6B7280]">Preview</div>
                        {/* Only needed when the source pane (and its toolbar copy) is hidden. */}
                        {bodyView === "preview" && <BodyViewToggle view={bodyView} onChange={setBodyView} where="preview"/>}
                      </div>
                      {/* The real sheet — stamp-paper header, A4 proportions, actual pagination —
                          not a bespoke box. It's the same DocumentSheet the customer's case page and
                          the wizard's live preview use, so an admin judging alignment (where a
                          heading sits relative to the header band, how a page breaks) is judging the
                          thing the customer will actually see, not an approximation of it. */}
                      <div className={`${bodyView === "split" ? "h-[420px]" : BODY_EXPANDED_H} flex flex-col`} data-testid="tpl-body-preview">
                        <DocumentSheet fit="container" body={editing.body_markdown}/>
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
              <div className="text-xs text-[#F5A524] bg-[#F5A524]/10 border border-[#F5A524]/40 rounded p-2">
                Saving takes effect immediately, on new and existing documents alike. Documents you
                have already edited individually keep your edit and are unaffected.
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button type="button" variant="outline" data-testid="tpl-edit-cancel" disabled={busy}
                        onClick={() => setEditing(null)}>
                  Cancel
                </Button>
                <Button data-testid="tpl-edit-save" disabled={busy} onClick={saveEdit}
                        className="bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190]">
                  {busy ? "Saving…" : creating ? "Create" : "Save"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
